import { createContext, useContext, useState, useEffect } from "react";
import { getMe } from "@/lib/api";

const AuthContext = createContext(null);

// Safe localStorage access
const safeLocalStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.error("localStorage.getItem error:", e);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.error("localStorage.setItem error:", e);
      return false;
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (e) {
      console.error("localStorage.removeItem error:", e);
      return false;
    }
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = safeLocalStorage.getItem("token");
    
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      // BACKGROUND call - no toast on error
      const res = await getMe();
      setUser(res.data);
    } catch (err) {
      // Silent handling - only log, no toast
      console.error("Auth check failed:", err.response?.status);
      
      // Only clear on 401, use cached data for network errors
      if (err.response?.status === 401) {
        safeLocalStorage.removeItem("token");
        safeLocalStorage.removeItem("user");
      } else {
        // Try cached user
        const cachedUser = safeLocalStorage.getItem("user");
        if (cachedUser) {
          try {
            setUser(JSON.parse(cachedUser));
          } catch (e) {
            // Invalid cache, ignore
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const loginUser = (token, userData) => {
    safeLocalStorage.setItem("token", token);
    safeLocalStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    safeLocalStorage.removeItem("token");
    safeLocalStorage.removeItem("user");
    setUser(null);
  };

  const isAdmin = user?.role === "Admin";
  const isStaff = user?.role === "Staff";
  const isProductionUser = user?.role === "production_user";

  return (
    <AuthContext.Provider value={{ user, loading, loginUser, logout, isAdmin, isStaff, isProductionUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
